import { useEffect, useState } from "react";

/**
 * Shared PWA-install state. The browser fires `beforeinstallprompt` ONCE and
 * usually early (before any route component mounts), so we capture it at module
 * load (this module is imported for its side-effects in index.js) and keep a
 * single deferred prompt that any component can use via `usePWAInstall()`.
 *
 * iOS Safari never fires the event — there `canInstall` stays false and the UI
 * falls back to "Share → Add to Home Screen" guidance.
 */
let _deferred = null;
let _installed = false;
const _subs = new Set();

function _isStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    window.navigator.standalone === true
  );
}

function _emit() {
  _subs.forEach((fn) => fn());
}

// Is the app running as an installed/standalone PWA (no browser chrome)?
export function isStandalone() {
  return _isStandalone();
}

if (typeof window !== "undefined") {
  if (_isStandalone()) _installed = true;
  window.addEventListener("beforeinstallprompt", (e) => {
    e.preventDefault();
    _deferred = e;
    _emit();
  });
  window.addEventListener("appinstalled", () => {
    _installed = true;
    _deferred = null;
    _emit();
  });
}

// ---- Tool open-mode (where a launched tool opens) ----
// "same"     → navigate the current window/PWA (default when standalone)
// "newtab"   → open a new browser tab (default in a normal browser)
// "newwindow"→ open a new popup window
export const OPEN_MODE_KEY = "tv_open_mode";
const OPEN_MODES = ["same", "newtab", "newwindow"];

export function getOpenMode() {
  try {
    const v = localStorage.getItem(OPEN_MODE_KEY);
    if (OPEN_MODES.includes(v)) return v;
  } catch {
    /* storage blocked */
  }
  // Installed PWA defaults to staying in the same window (the terminal); a
  // normal browser tab defaults to opening tools in a new tab.
  return _isStandalone() ? "same" : "newtab";
}

export function setOpenMode(mode) {
  if (!OPEN_MODES.includes(mode)) return;
  try {
    localStorage.setItem(OPEN_MODE_KEY, mode);
  } catch {
    /* storage blocked */
  }
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("tv:open-mode-changed", { detail: mode }));
  }
}

// React hook: current open mode, re-rendering when it changes anywhere.
export function useOpenMode() {
  const [mode, setMode] = useState(() => getOpenMode());
  useEffect(() => {
    const fn = () => setMode(getOpenMode());
    window.addEventListener("tv:open-mode-changed", fn);
    return () => window.removeEventListener("tv:open-mode-changed", fn);
  }, []);
  return mode;
}

// Launch a single tool URL honoring the chosen open mode.
export function launchTool(url, mode) {
  const m = OPEN_MODES.includes(mode) ? mode : getOpenMode();
  if (!url) return;
  // Tag the destination as a TerminalValue-launched tool window so the browser
  // extension recognises it (isWorkspaceWindow → name starts with "tv_ws") and
  // injects the TerminalValue menu bar there — matching how openManagedWindows
  // names workspace windows. window.name persists onto the new window / across
  // the same-window navigation. (No `noopener` so the name is applied reliably,
  // same as the working workspace-launch path.)
  const name = `tv_ws_tool_${Date.now()}`;
  if (m === "same") {
    try { window.name = name; } catch (e) {}
    window.location.href = url;
  } else if (m === "newwindow") {
    const sw = (window.screen && window.screen.availWidth) || window.innerWidth || 1280;
    const sh = (window.screen && window.screen.availHeight) || window.innerHeight || 800;
    const w = Math.floor(sw * 0.6);
    const h = Math.floor(sh * 0.8);
    window.open(url, name, `popup,width=${w},height=${h}`);
  } else {
    window.open(url, name);
  }
}

// How long the window-permission guidance toasts stay up (multi-step, so they
// need to linger).
export const POPUP_TOAST_DURATION = 14000;

// Guidance when no windows could open (Window Management permission denied or
// the pop-up blocker stepped in on a browser without the API).
export function popupHelp() {
  return 'Allow "window management" when the app asks, then click again — that lets it open and arrange all your windows. If you dismissed it, re-enable it in the app menu → Site settings → Permissions.';
}

// Open a set of windows tiled across the current screen. Uses the Window
// Management API when available — this shows a clean in-app permission prompt
// (works inside an installed PWA, no address bar needed) and, once granted,
// opens MULTIPLE windows without the pop-up blocker and lets us place them
// precisely. Falls back to plain window.open on unsupported browsers.
// `items`: [{ url, name }]. Returns the number of windows that opened.
export async function openManagedWindows(items) {
  const list = (items || []).filter((it) => it && it.url);
  const n = list.length;
  if (!n) return 0;

  // Resolve the usable screen rectangle. getScreenDetails() triggers the
  // `window-management` permission prompt the first time; once granted it also
  // un-gates multi-window window.open in the same gesture.
  let area = null;
  if (typeof window !== "undefined" && "getScreenDetails" in window) {
    try {
      const details = await window.getScreenDetails();
      const s = details.currentScreen || (details.screens && details.screens[0]);
      if (s) {
        area = {
          left: s.availLeft || 0,
          top: s.availTop || 0,
          width: s.availWidth,
          height: s.availHeight,
        };
      }
    } catch {
      /* denied / unsupported — fall back to window.screen below */
    }
  }
  if (!area) {
    area = {
      left: (window.screen && window.screen.availLeft) || 0,
      top: (window.screen && window.screen.availTop) || 0,
      width: (window.screen && window.screen.availWidth) || window.innerWidth || 1280,
      height: (window.screen && window.screen.availHeight) || window.innerHeight || 800,
    };
  }

  const cols = n <= 1 ? 1 : 2;
  const rows = Math.ceil(n / cols);
  const w = Math.floor(area.width / cols);
  const h = Math.floor(area.height / rows);
  let opened = 0;
  for (let i = 0; i < n; i++) {
    const col = i % cols;
    const row = Math.floor(i / cols);
    const left = area.left + col * w;
    const top = area.top + row * h;
    const features = `popup,width=${w},height=${h},left=${left},top=${top}`;
    const ref = window.open(list[i].url, list[i].name || `tv_${i}`, features);
    if (ref) {
      opened += 1;
      try {
        ref.moveTo(left, top);
        ref.resizeTo(w, h);
        ref.focus();
      } catch {
        /* cross-origin window control may throw — placement still applied via features */
      }
    }
  }
  return opened;
}


export function usePWAInstall() {
  const [, force] = useState(0);
  useEffect(() => {
    const fn = () => force((n) => n + 1);
    _subs.add(fn);
    return () => _subs.delete(fn);
  }, []);

  const promptInstall = async () => {
    if (!_deferred) return false;
    _deferred.prompt();
    try {
      await _deferred.userChoice;
    } catch {
      /* user dismissed */
    }
    _deferred = null;
    _emit();
    return true;
  };

  return { canInstall: !!_deferred && !_installed, installed: _installed, promptInstall };
}
